/**
 * Si un archivo elegido en el formulario YA NO se puede leer.
 *
 * 🔴 El fallo que motivó esto (9-set, registro público): en Android, el `File` que sale de un
 * `<input type="file">` no contiene los bytes — es un puntero al archivo del sistema. Si la
 * postulante toma la foto con la cámara y después llena cinco pasos de formulario, el sistema
 * operativo puede reciclar ese archivo temporal, y el puntero queda apuntando a la nada.
 *
 * Lo grave era CÓMO se manifestaba:
 *
 *  - `file.name` y `file.size` siguen respondiendo (son metadatos ya copiados), así que la pantalla
 *    seguía mostrando «Listo: 1000081544.jpg» en verde. Nada avisaba.
 *  - `createImageBitmap` fallaba al leerlo y el `catch` de `compressImageResult` devolvía el blob
 *    original —muerto— con un `console.warn` que nadie ve en un celular.
 *  - `fetch` hacía el preflight de CORS (OPTIONS 200) y al ir a leer el cuerpo se caía, así que
 *    **el POST nunca salía del teléfono**. En los logs del servidor quedó exactamente eso: el
 *    OPTIONS con 200 y ningún POST detrás.
 *  - `storage-js` LANZA los errores que no son suyos, así que se salteaba el mensaje bueno y la
 *    pantalla imprimía «Failed to fetch» en inglés.
 *
 * Y como reenviar el formulario reusa el mismo `File`, **fallaba para siempre**: hay un caso real
 * de 40 intentos seguidos, y solo funcionó cuando la persona volvió a elegir las fotos.
 *
 * ## Por qué la función se llama en NEGATIVO
 *
 * Porque solo puede bloquear con **evidencia positiva** de que el archivo está muerto, y en la
 * duda tiene que dejar pasar.
 *
 * La primera versión preguntaba «¿es legible?» y devolvía `false` cuando no podía comprobarlo. En
 * un entorno sin `Blob.arrayBuffer` eso convertía «no sé» en «está roto» y **habría bloqueado
 * TODOS los registros**, que es infinitamente peor que el fallo que vino a arreglar. Lo cazaron las
 * pruebas viejas, no el diseño: cinco casos que nada tenían que ver empezaron a fallar con el
 * mensaje de archivo ilegible.
 */
export async function archivoIlegible(blob: Blob | null | undefined): Promise<boolean> {
  // No hay archivo: eso sí es un problema, y quien llama ya sabe cómo decirlo.
  if (!blob) return true;
  // Cero bytes es la otra cara del mismo puntero roto, y se sabe sin leer nada.
  if (blob.size === 0) return true;

  if (typeof blob.slice !== 'function') return false;
  let trozo: Blob;
  try {
    trozo = blob.slice(0, 1);
  } catch {
    return true;
  }
  // Entorno sin `arrayBuffer` (jsdom viejo, navegador antiguo): NO se puede comprobar, así que no
  // se bloquea. Leer un byte es una mejora del diagnóstico, no un requisito para registrarse.
  if (typeof trozo.arrayBuffer !== 'function') return false;
  try {
    await trozo.arrayBuffer();
    return false;
  } catch {
    // El puntero está roto: `arrayBuffer` rechaza acá y no adentro de una subida de 300 KB.
    return true;
  }
}

/**
 * El error de un archivo que ya no se puede leer.
 *
 * Es una clase aparte porque su mensaje tiene que decir algo DISTINTO de «revisa tu conexión»: la
 * conexión está perfecta —el preflight respondió 200— y lo único que saca a la persona del
 * problema es volver a elegir el archivo. Un «reintentar» no sirve, y eso fue exactamente lo que
 * hizo que alguien reintentara cuarenta veces.
 */
export class ArchivoIlegibleError extends Error {
  constructor(que: string) {
    super(
      `No se pudo leer ${que} desde tu teléfono. El archivo que elegiste ya no está disponible `
      + '—pasa cuando la foto se tomó hace un rato—. Vuelve a tomar la foto y adjúntala de nuevo.',
    );
    this.name = 'ArchivoIlegibleError';
  }
}
